/**
 * LinkedIn OIDC PKCE helper. PKCE state machine is identical to
 * puminet4/src/auth/linkedin.js — same sessionStorage keys, same helpers,
 * same authorization URL shape. The one deliberate divergence is the
 * `buildLinkedInAuthUrl` error message: betonijerry's SocialLoginButtons
 * surfaces err.message directly to the user via setError, so the missing-
 * env-var case throws a Finnish user-facing string. puminet4's button
 * overrides err.message with a fixed Finnish notification, so its helper
 * throws an English developer-facing string instead.
 *
 * No JS SDK — LinkedIn uses a plain HTTP redirect. crypto.subtle for SHA-256
 * is browser-native (HTTPS-only outside localhost).
 */
const STATE_KEY = "linkedin_oauth_state";
const VERIFIER_KEY = "linkedin_pkce_verifier";
const SOURCE_KEY = "linkedin_oauth_source";

function base64url(bytes) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function sha256base64url(input) {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return base64url(new Uint8Array(hash));
}

function randomBase64url(len = 32) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

/**
 * Build the LinkedIn authorization URL. Caller `window.location.assign`s it
 * to start the redirect flow. Generates fresh state + PKCE verifier on every
 * call and stashes them in sessionStorage for the callback to consume.
 *
 * @param {{redirectUri: string, source?: string, t: (key: string) => string}} args — `t` is
 *   threaded in explicitly since this module has no React/hook access (see src/i18n's
 *   shared procedure).
 * @returns {Promise<string>}
 */
export async function buildLinkedInAuthUrl({ redirectUri, source, t } = {}) {
  const clientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID;
  if (!clientId) throw new Error(t("common.auth.linkedinNotConfigured"));

  const state = crypto.randomUUID();
  const verifier = randomBase64url(32);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  if (source) sessionStorage.setItem(SOURCE_KEY, source);

  const challenge = await sha256base64url(verifier);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: "openid profile email",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
}

/**
 * Read+clear the state + verifier + source the callback page needs to
 * complete the exchange. Returns `null` fields if the values are missing
 * (the caller treats this as a state-mismatch error).
 *
 * @returns {{state: string|null, verifier: string|null, source: string|null}}
 */
export function consumeLinkedInCallbackState() {
  const state = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const source = sessionStorage.getItem(SOURCE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(SOURCE_KEY);
  return { state, verifier, source };
}

export const LINKEDIN_CALLBACK_PATH = "/oauth/linkedin/callback";
